import { LoanCalculations } from './loan-calculations'
import { IAmortizationSchedule } from './amortization-schedule'

export type MCGBPaymentScheduleItem = {
  period: number
  date: Date
  daysInPeriod: number
  outstandingPrincipal: number
  customerPaymentAmount: number
  isIncentivePeriod: boolean
  // Monthly amount paid by MCGB= standard_monthly_payment − incentive_monthly_payment
  // (during incentive payment period only)
  mcgbPaidAmount: number
}

export type MCGBAmortizationResult = {
  incentiveSchedule: MCGBPaymentScheduleItem[]
  incentiveMonthlyPayment: number
  standardMonthlyPayment: number
  totalMCGBIncentive: number
  customerFinanceCharge: number
  customerTotalOfPayments: number
}

export class MCGBLoanCalculations {

  static readonly INCENTIVE_RATE = 2.99
  static readonly INCENTIVE_MONTHS = 15 * 12

  // Calculate MCGB incentive loan schedule
  static calculate(
    term: number,
    loanAmount: number,
    fees: number,
    standardRate: number,
    interestOnlyPayments = 6,
    loanStartDate?: Date,
  ): MCGBAmortizationResult {

    // 1. Calculate standard amortization at full rate
    const standardSchedule = LoanCalculations.getPaymentAndAmortizationSchedule(
      term,
      loanAmount,
      fees,
      standardRate,
      interestOnlyPayments,
      loanStartDate
    )

    // 2. Calculate loan amount at MCBG incentive rate
    const mcgbSchedule = LoanCalculations.getPaymentAndAmortizationSchedule(
      term,
      loanAmount,
      fees,
      this.INCENTIVE_RATE,
      interestOnlyPayments,
      loanStartDate
    )

    // 3. Build combined schedule with MCGB subsidy logic
    // (payment-based: MCGB pays standard − incentive payment difference)
    const incentiveSchedule = this.buildIncentiveSchedule(
      standardSchedule,
      mcgbSchedule,
      interestOnlyPayments
    )

    // 4. Calculate totals
    const totalMCGBIncentive = this.sumMCGBPayments(incentiveSchedule)
    const {
      customerFinanceCharge,
      customerTotalOfPayments
    } = this.calculateCustomerTotals(incentiveSchedule, loanAmount)

    return {
      incentiveSchedule,
      incentiveMonthlyPayment: Number(mcgbSchedule.payment.toFixed(2)),
      standardMonthlyPayment: Number(standardSchedule.payment.toFixed(2)),
      totalMCGBIncentive,
      customerFinanceCharge,
      customerTotalOfPayments,
    }
  }

  /**
   * APR for loans with incentives using IRR.
   * Funding to the borrower is principal only: period 1 = 50%, period 5 = 50%.
   * Financed fees are a finance charge. They are already included in `payments`
   * and are not treated as cash the borrower receives.
   * Returns APR as a percentage (e.g. 8.5 for 8.5%).
   */
  static calculateIncentiveAPR(
    loanAmount: number,
    payments: number[]
  ): number {
    if (payments.length === 0) {
      return 0
    }
    const disbursements = this.buildDisbursementSchedule(loanAmount, payments.length)
    const totalDisbursed = disbursements.reduce((a, b) => a + b, 0)
    const totalPaid = payments.reduce((a, b) => a + b, 0)
    if (totalDisbursed <= 0 || totalPaid <= 0) {
      return 0
    }

    // Net Present Value (NPV) at a given monthly rate r:
    const npv = (r: number): number => {
      let pv = 0
      for (let t = 0; t < payments.length; t++) {
        const cf = disbursements[t] - payments[t]
        pv += cf / Math.pow(1 + r, t)
      }
      return pv
    }

    // Slope of NPV with respect to r (derivative):
    // Used by Newton-Raphson to know "which way to move r" so we converge quickly
    // to the rate that makes NPV = 0.
    const npvDerivative = (r: number): number => {
      let dv = 0
      for (let t = 0; t < payments.length; t++) {
        const cf = disbursements[t] - payments[t]
        dv -= (t * cf) / Math.pow(1 + r, t + 1)
      }
      return dv
    }

    // Find the monthly rate r that makes NPV = 0 (the IRR):
    // Start with a simple guess based on total paid vs total disbursed, then refine
    // using Newton-Raphson: each step we move r in the direction that reduces NPV
    // toward zero. Stop when NPV is close enough or we hit limits.
    let r = ((totalPaid / totalDisbursed) - 1) / Math.max(1, payments.length)
    r = Math.max(1e-6, Math.min(r, 0.5))
    const precision = 1e-9
    const maxIterations = 100
    for (let i = 0; i < maxIterations; i++) {
      const n = npv(r)
      if (Math.abs(n) < precision) break
      const d = npvDerivative(r)
      if (Math.abs(d) < 1e-15) break
      r = r - (n / d)
      if (r <= 0) r = 1e-6
      if (r >= 1) r = 0.99
    }

    // Convert monthly rate to annual percentage (e.g. 0.01 -> 12%) and clamp to 0–100%.
    const aprPercent = r * 12 * 100
    return Math.max(0, Math.min(100, aprPercent))
  }

  /**
   * Cash the borrower receives, used for the incentive APR.
   * Period 1: 50% of principal. Period 5: the other 50%.
   * Fees are omitted. The amortization schedule still adds them to the balance
   * at period 0, and that cost shows up in the customer payment.
   */
  private static buildDisbursementSchedule(
    loanAmount: number,
    numPeriods: number
  ): number[] {
    const disbursements = new Array<number>(numPeriods).fill(0)
    if (numPeriods > 1) disbursements[1] = loanAmount / 2
    if (numPeriods > 5) disbursements[5] = loanAmount / 2
    return disbursements
  }

  /**
   * Build the combined schedule showing borrower vs MCGB payments.
   * MCGB subsidy = standard_monthly_payment − incentive_monthly_payment for each payment month in the incentive period.
   * Customer pays incentive payment (2.99%) during incentive period, standard payment after.
   */
  private static buildIncentiveSchedule(
    standardSchedule: IAmortizationSchedule,
    mcgbSchedule: IAmortizationSchedule,
    interestOnlyPayments: number
  ): MCGBPaymentScheduleItem[] {
    const standardMonthlyPayment = standardSchedule.payment
    const incentiveMonthlyPayment = mcgbSchedule.payment
    const mcgbPaidPerMonth = standardMonthlyPayment - incentiveMonthlyPayment

    return standardSchedule.schedule.map((item, index) => {
      const isIncentivePeriod = index <= this.INCENTIVE_MONTHS
      const isInterestOnlyPeriod = index <= interestOnlyPayments
      const isIncentivePaymentPeriod = isIncentivePeriod && !isInterestOnlyPeriod


      // MCGB pays (standard payment − incentive payment) only during 
      // incentive payment months (after 6-month no-payment)
      const mcgbPaidAmount = isIncentivePaymentPeriod ? Math.max(0, mcgbPaidPerMonth) : 0

      // Customer: 0 during no-payment, incentive payment during incentive period, standard payment after
      let customerPaymentAmount: number
      if (isInterestOnlyPeriod) {
        customerPaymentAmount = 0
      } else if (isIncentivePaymentPeriod) {
        customerPaymentAmount = incentiveMonthlyPayment
      } else {
        customerPaymentAmount = standardMonthlyPayment
      }

      return {
        period: index,
        date: item.date,
        daysInPeriod: item.daysInPeriod,
        outstandingPrincipal: item.outstandingPrincipal,
        mcgbPaidAmount,
        customerPaymentAmount: Math.max(0, customerPaymentAmount),
        isIncentivePeriod,
      }
    })
  }
  
  private static sumMCGBPayments(schedule: MCGBPaymentScheduleItem[]): number {
    return schedule.reduce((sum, item) => sum + item.mcgbPaidAmount, 0)
  }

  private static calculateCustomerTotals(
    schedule: MCGBPaymentScheduleItem[],
    loanAmount: number
  ): { customerFinanceCharge: number, customerTotalOfPayments: number } {

    const customerTotalOfPayments = schedule.reduce(
      (sum, item) => sum + Number(item.customerPaymentAmount), 0
    )

    const customerFinanceCharge = customerTotalOfPayments - loanAmount

    return { customerFinanceCharge, customerTotalOfPayments }
  }
}